import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { X, VideoOff } from 'lucide-react';
import { FeatureListSection } from './FeatureListSection';
import { VideoFeatures } from '@/store/useVideoFeatures';
import { cn } from '@/lib/utils';
import { useDrawer } from '@/contexts/DrawerContext';

interface NewVideoLayoutProps {
  children: React.ReactNode;
  features: VideoFeatures;
  onFeatureClick: (featureId: string) => void;
  showResourcesSidebar?: boolean;
  activeEditingPanel?: string | null;
  currentVideo?: {
    id: string;
    title: string;
    thumbnailUrl: string;
  } | null;
  onChangeVideo?: () => void;
}

export const NewVideoLayout: React.FC<NewVideoLayoutProps> = ({
  children,
  features,
  onFeatureClick,
  showResourcesSidebar = false,
  activeEditingPanel,
  currentVideo,
  onChangeVideo
}) => {
  useLanguage();
  const { closeDrawer, isOpen } = useDrawer();
  const navigate = useNavigate();

  // Redirecionar para o dashboard ao clicar na logo
  const handleLogoClick = () => {
    navigate('/dashboard');
  };

  // Fechar drawer ao clicar em uma feature (mobile)
  const handleFeatureClickWithClose = (featureId: string) => {
    onFeatureClick(featureId);
    closeDrawer();
  };

  // Atalhos de teclado
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+F foca campo de busca
      if ((e.ctrlKey || e.metaKey) && e.key === 'f' && showResourcesSidebar) {
        e.preventDefault();
        const searchInput = document.querySelector('[placeholder="Buscar recurso..."]') as HTMLInputElement;
        searchInput?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showResourcesSidebar]);

  // Renderizar conteúdo de recursos no drawer global (mobile)
  const [drawerContentContainer, setDrawerContentContainer] = useState<HTMLElement | null>(null);

  // Buscar o container do drawer sempre que o drawer abrir
  useEffect(() => {
    // Limpar container quando drawer fecha
    if (!isOpen) {
      setDrawerContentContainer(null);
      return;
    }

    if (typeof document === 'undefined') return;

    let attempts = 0;
    const maxAttempts = 60; // 60 tentativas * 100ms = 6 segundos

    const checkContainer = () => {
      attempts++;
      const container = document.getElementById('personalization-drawer-content');

      if (container) {
        setDrawerContentContainer(container);
        return true;
      }
      return false;
    };

    // Verificar imediatamente
    if (checkContainer()) return;

    // Se não encontrar, verificar em intervalos
    const interval = setInterval(() => {
      if (checkContainer() || attempts >= maxAttempts) {
        clearInterval(interval);
      }
    }, 100);

    return () => {
      clearInterval(interval);
    };
  }, [isOpen]);

  return (
    <>
      {/* Desktop: sidebar de recursos + conteúdo, ambos flutuando sobre o canvas.
          O padding daqui é o que cria a moldura; por isso os filhos usam h-full. */}
      <div className="hidden lg:flex h-screen w-full overflow-hidden gap-4 p-3 bg-[#F1F4F9]">
        {showResourcesSidebar && (
          <aside
            className="w-[280px] xl:w-[300px] min-w-[260px] max-w-[320px] h-full overflow-hidden flex flex-col shrink-0 bg-card rounded-[16px] border border-[#E1E7F0] shadow-[0_8px_30px_rgba(15,23,42,0.08)]"
            style={{ contain: 'layout' }}
            aria-label={translateInterfaceText("Sidebar de recursos do vídeo")}
          >
              {/* Header fixo com Logo HostVSL. A linha de baixo fica rente à do
                  Header do conteúdo: hoje o index.css força `.sidebar-header, header`
                  a 64px, e os 73px daqui (min-h 72px + border-b do Header no modo
                  personalização) mantêm o alinhamento se essa regra sair.
                  border-b-[1px] em vez de border-b: o index.css pinta
                  `aside .border-b` de #E5E7EB (!important), e a linha precisa da
                  mesma cor do Header (border-border, que também muda no dark). */}
              <div className="sidebar-header px-6 bg-card sticky top-0 z-20 flex items-center justify-center h-[73px] border-b-[1px] border-border">
                <img
                  src="/assets/imgs/logo-azul-e-preta.png"
                  className="h-7 cursor-pointer hover:opacity-80 transition-opacity"
                  alt={translateInterfaceText("HostVSL")}
                  onClick={handleLogoClick}
                />
              </div>

            {/* Features com scroll independente */}
            <div className="flex-1 overflow-hidden">
              <FeatureListSection
                features={features}
                onFeatureClick={onFeatureClick}
                activeEditingPanel={activeEditingPanel}
                currentVideo={currentVideo}
                onChangeVideo={onChangeVideo}
              />
            </div>
          </aside>
        )}

        {/* Área principal (conteúdo) — módulo flutuante */}
        <main className="flex-1 min-w-0 h-full overflow-hidden flex flex-col rounded-[16px] bg-card border border-border shadow-[0_4px_18px_hsl(var(--foreground)/0.04)]">
          {children}
        </main>
      </div>

      {/* Tablet/Mobile: Usa drawer global do Layout */}
      <div className="lg:hidden flex flex-col h-screen w-full overflow-hidden">
        {/* Injeta conteúdo de recursos no drawer global via portal */}
        {drawerContentContainer && ReactDOM.createPortal(
          showResourcesSidebar ? (
            <div className="h-full flex flex-col bg-background">
              {/* Header com Logo */}
              <div className="sidebar-header px-6 py-5 border-b flex items-center justify-between bg-card flex-shrink-0 h-[78px]">
                <img
                  src="/assets/imgs/logo-azul-e-preta.png"
                  className="h-6 cursor-pointer hover:opacity-80 transition-opacity"
                  alt={translateInterfaceText("HostVSL")}
                  onClick={handleLogoClick}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={closeDrawer}
                  aria-label={translateInterfaceText("Fechar recursos")}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              {/* Lista de recursos com scroll */}
              <div className="flex-1 overflow-y-auto pb-4">
                {features ? (
                  <FeatureListSection
                    features={features}
                    onFeatureClick={handleFeatureClickWithClose}
                    activeEditingPanel={activeEditingPanel}
                    currentVideo={currentVideo}
                    onChangeVideo={onChangeVideo}
                  />
                ) : (
                  <div className="p-4 text-sm text-muted-foreground">
                    {translateInterfaceText("Carregando recursos...")}</div>
                )}
              </div>
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center gap-3 p-6 text-center">
              <VideoOff className="h-10 w-10 text-muted-foreground/40" />
              <p className="text-sm font-medium text-muted-foreground">{translateInterfaceText("Nenhum vídeo selecionado")}</p>
              <p className="text-xs text-muted-foreground/60">{translateInterfaceText("Selecione um vídeo para acessar os recursos de personalização.")}</p>
            </div>
          ),
          drawerContentContainer
        )}

        <main className="flex-1 overflow-hidden flex flex-col">
          {children}
        </main>
      </div>
    </>
  );
};
